import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSidebar } from '../../context/SidebarContext';
import { useAuth } from '../../context/AuthContext';
import { AlertNavBadge } from '../AlertNavBadge';
import {
  DashboardIcon,
  CampaignIcon,
  CreativeBoardIcon,
  ChartIcon,
  AlertIcon,
  SettingsIcon,
  AutomationIcon,
  CreativeIcon,
  ChevronDownIcon,
} from '../icons/NavigationIcons';

// Custom AI Icon
const AIIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

// Target/Audience Icon
const AudienceIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

// Sparkles/NEW Icon
const SparklesIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
  </svg>
);

interface MenuItem {
  label: string;
  path: string;
  icon: React.FC<{ className?: string }>;
  badge?: string;
  badgeColor?: string;
  disabled?: boolean;
  submenu?: MenuItem[];
}

// =====================================================
// MADGICX-STYLE NAVIGATION STRUCTURE
// =====================================================
const menuItems: MenuItem[] = [
  // ─────────────────────────────────────────────────
  // AI FEATURES (TOP - Most Important)
  // ─────────────────────────────────────────────────
  {
    label: 'AI Assistant',
    path: '/ai-chat',
    icon: AIIcon,
    badge: 'Beta',
    badgeColor: 'bg-blue-600',
  },
  {
    label: 'AI Ad Studio',
    path: '/ai-copy-studio',
    icon: SparklesIcon,
    badge: 'NEW!',
    badgeColor: 'bg-green-600',
  },

  // ─────────────────────────────────────────────────
  // ANALYTICS - All reporting & insights
  // ─────────────────────────────────────────────────
  {
    label: 'Analytics',
    path: '/analytics',
    icon: ChartIcon,
    submenu: [
      { label: 'Dashboard Overview', path: '/analytics', icon: DashboardIcon },
      { label: 'Platform Comparison', path: '/platform-comparison', icon: ChartIcon },
      { label: 'Spend Analytics', path: '/spend', icon: ChartIcon },
      { label: 'Demographics', path: '/demographics', icon: ChartIcon },
      { label: 'Conversion Funnel', path: '/analytics/funnel', icon: ChartIcon },
      { label: 'Trend Indicators', path: '/trends', icon: ChartIcon },
      { label: 'Custom Metrics', path: '/custom-metrics', icon: ChartIcon },
      { label: 'ML Learning Center', path: '/ml-dashboard', icon: AIIcon },
      { label: 'Reports Builder', path: '/reports/builder', icon: ChartIcon },
    ],
  },

  // ─────────────────────────────────────────────────
  // CREATIVES - All creative management
  // ─────────────────────────────────────────────────
  {
    label: 'Creatives',
    path: '/creative-library',
    icon: CreativeIcon,
    submenu: [
      { label: 'AI Copy Generator', path: '/ai-copy-studio', icon: SparklesIcon },
      { label: 'Copy Performance', path: '/ai-copy/performance', icon: ChartIcon },
      { label: 'Creative Library', path: '/creative-library', icon: CreativeIcon },
      { label: 'Creative Boards', path: '/boards', icon: CreativeBoardIcon },
      { label: 'Tone Analytics', path: '/tone-analytics', icon: CreativeIcon },
    ],
  },

  // ─────────────────────────────────────────────────
  // CAMPAIGNS - Campaign management
  // ─────────────────────────────────────────────────
  {
    label: 'Campaigns',
    path: '/campaigns',
    icon: CampaignIcon,
    submenu: [
      { label: 'All Campaigns', path: '/campaigns', icon: CampaignIcon },
      { label: 'Create with AI ✨', path: '/campaigns/create', icon: SparklesIcon },
      { label: 'Budget Pacing', path: '/budget-pacing', icon: ChartIcon },
      { label: 'Budget Adjustment', path: '/budget-adjustment', icon: ChartIcon },
    ],
  },

  // ─────────────────────────────────────────────────
  // AUDIENCES - Audience targeting & management
  // ─────────────────────────────────────────────────
  {
    label: 'Audiences',
    path: '/audiences',
    icon: AudienceIcon,
    submenu: [
      { label: 'Audience Manager', path: '/audiences', icon: AudienceIcon },
    ],
  },

  // ─────────────────────────────────────────────────
  // AUTOMATION - Rules, tests, AI optimization
  // ─────────────────────────────────────────────────
  {
    label: 'Automation',
    path: '/automation',
    icon: AutomationIcon,
    submenu: [
      { label: 'Auto-Pause Rules', path: '/auto-pause-rules', icon: AutomationIcon },
      { label: 'A/B Test Manager', path: '/ab-tests', icon: AutomationIcon },
      { label: 'AI Opportunities', path: '/opportunities', icon: SparklesIcon },
    ],
  },

  // ─────────────────────────────────────────────────
  // ALERTS & ACTIVITY - Monitoring & logs
  // ─────────────────────────────────────────────────
  {
    label: 'Alerts & Activity',
    path: '/alerts',
    icon: AlertIcon,
    submenu: [
      { label: 'Alert Dashboard', path: '/alerts', icon: AlertIcon },
      { label: 'Approvals Queue', path: '/approvals', icon: AlertIcon },
      { label: 'Activity Log', path: '/activity', icon: AlertIcon },
    ],
  },

  // ─────────────────────────────────────────────────
  // SETTINGS - Configuration & preferences
  // ─────────────────────────────────────────────────
  {
    label: 'Settings',
    path: '/settings/profile',
    icon: SettingsIcon,
    submenu: [
      { label: 'Profile', path: '/settings/profile', icon: SettingsIcon },
      { label: 'Ad Accounts', path: '/settings/ad-accounts', icon: SettingsIcon },
      { label: 'Team Management', path: '/settings/team', icon: AudienceIcon },
      { label: 'Integrations', path: '/integrations', icon: SettingsIcon },
      { label: 'API Keys', path: '/settings/api-keys', icon: SettingsIcon },
      { label: 'Webhooks', path: '/settings/webhooks', icon: SettingsIcon },
    ],
  },
];

export const AppSidebar: React.FC = () => {
  const { isExpanded, isMobileOpen, setIsHovered, toggleSidebar, toggleMobileSidebar } = useSidebar();
  const { user } = useAuth();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);

  // Generate user initials from full name
  const getInitials = (name: string | null | undefined) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const toggleSubmenu = (label: string) => {
    setExpandedMenus((prev) =>
      prev.includes(label) ? prev.filter((item) => item !== label) : [...prev, label]
    );
  };

  const isActive = (path: string) => {
    if (path === '/' || path === '#') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const isSubmenuActive = (submenu?: MenuItem[]) => {
    if (!submenu) return false;
    return submenu.some((item) => isActive(item.path));
  };

  return (
    <>
      {/* Mobile Backdrop */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={toggleMobileSidebar}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed left-0 top-0 z-50 h-screen
          ${isExpanded ? 'w-72' : 'w-20'}
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          bg-black
          border-r border-gray-800
          transition-all duration-300 ease-in-out
          flex flex-col overflow-hidden
        `}
        onMouseEnter={() => !isMobileOpen && setIsHovered(true)}
        onMouseLeave={() => !isMobileOpen && setIsHovered(false)}
      >
        {/* Logo & Toggle */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-800">
          <Link to="/" className="flex items-center space-x-3 overflow-hidden">
            <img 
              src="/horizonbg-logo.svg" 
              alt="HorizonBG" 
              className="h-8 w-auto flex-shrink-0"
            />
            {isExpanded && (
              <span className="text-white font-semibold text-lg whitespace-nowrap">
                GP Ads AI
              </span>
            )}
          </Link>

          <button
            onClick={toggleSidebar}
            className="hidden lg:flex items-center justify-center w-8 h-8 rounded-lg
                     hover:bg-gray-800
                     text-white transition-colors"
            aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            <ChevronDownIcon
              className={`w-5 h-5 transition-transform ${
                isExpanded ? 'rotate-180' : 'rotate-0'
              }`}
            />
          </button>

          {/* Mobile Close Button */}
          <button
            onClick={toggleMobileSidebar}
            className="lg:hidden flex items-center justify-center w-8 h-8 rounded-lg
                     hover:bg-gray-800
                     text-white transition-colors"
            aria-label="Close sidebar"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-4 px-3 space-y-1
                      scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
          {menuItems.map((item) => {
            const hasSubmenu = item.submenu && item.submenu.length > 0;
            const isMenuExpanded = expandedMenus.includes(item.label);
            const isMenuActive = isActive(item.path) || isSubmenuActive(item.submenu);
            const isDisabled = item.disabled;

            return (
              <div key={item.label}>
                {/* Main Menu Item */}
                {hasSubmenu ? (
                  <button
                    onClick={() => toggleSubmenu(item.label)}
                    className={`
                      w-full flex items-center justify-between rounded-lg px-3 py-2.5
                      transition-colors group relative
                      ${
                        isMenuActive
                          ? 'bg-[#CB3B0C] text-white'
                          : 'text-white hover:bg-gray-800'
                      }
                    `}
                  >
                    <div className="flex items-center space-x-3 overflow-hidden">
                      <div className="relative">
                        <item.icon
                          className="w-5 h-5 flex-shrink-0"
                        />
                        {item.label === 'Alerts & Activity' && <AlertNavBadge />}
                      </div>
                      {isExpanded && (
                        <span className="font-medium whitespace-nowrap">{item.label}</span>
                      )}
                    </div>
                    {isExpanded && (
                      <ChevronDownIcon
                        className={`w-4 h-4 flex-shrink-0 transition-transform ${
                          isMenuExpanded ? 'rotate-180' : 'rotate-0'
                        }`}
                      />
                    )}

                    {/* Tooltip for collapsed state */}
                    {!isExpanded && (
                      <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded
                                    opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50
                                    transition-opacity">
                        {item.label}
                      </div>
                    )}
                  </button>
                ) : isDisabled ? (
                  // Disabled item (AI Assistant - Coming Soon)
                  <div
                    className={`
                      w-full flex items-center rounded-lg px-3 py-2.5
                      transition-colors group relative
                      text-gray-500 cursor-not-allowed
                    `}
                  >
                    <div className="flex items-center space-x-3 overflow-hidden">
                      <item.icon className="w-5 h-5 flex-shrink-0" />
                      {isExpanded && (
                        <>
                          <span className="font-medium whitespace-nowrap">{item.label}</span>
                          {item.badge && (
                            <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${item.badgeColor} text-white`}>
                              {item.badge}
                            </span>
                          )}
                        </>
                      )}
                    </div>

                    {/* Tooltip for collapsed state */}
                    {!isExpanded && (
                      <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded
                                    opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50
                                    transition-opacity">
                        {item.label} ({item.badge})
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    onClick={() => isMobileOpen && toggleMobileSidebar()}
                    className={`
                      w-full flex items-center rounded-lg px-3 py-2.5
                      transition-colors group relative
                      ${
                        isMenuActive
                          ? 'bg-[#CB3B0C] text-white'
                          : 'text-white hover:bg-gray-800'
                      }
                    `}
                  >
                    <div className="flex items-center space-x-3 overflow-hidden">
                      <item.icon className="w-5 h-5 flex-shrink-0" />
                      {isExpanded && (
                        <>
                          <span className="font-medium whitespace-nowrap">{item.label}</span>
                          {item.badge && (
                            <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${item.badgeColor} text-white font-semibold`}>
                              {item.badge}
                            </span>
                          )}
                        </>
                      )}
                    </div>

                    {/* Tooltip for collapsed state */}
                    {!isExpanded && (
                      <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded
                                    opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50
                                    transition-opacity">
                        {item.label} {item.badge && `(${item.badge})`}
                      </div>
                    )}
                  </Link>
                )}

                {/* Submenu */}
                {hasSubmenu && isExpanded && isMenuExpanded && (
                  <div className="mt-1 ml-4 pl-4 border-l-2 border-gray-700 space-y-1">
                    {item.submenu!.map((subItem) => (
                      <Link
                        key={subItem.path + subItem.label}
                        to={subItem.path}
                        onClick={() => isMobileOpen && toggleMobileSidebar()}
                        className={`
                          flex items-center space-x-3 rounded-lg px-3 py-2 text-sm
                          transition-colors
                          ${
                            isActive(subItem.path)
                              ? 'bg-[#CB3B0C] text-white font-medium'
                              : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                          }
                        `}
                      >
                        <subItem.icon className="w-4 h-4 flex-shrink-0" />
                        <span className="whitespace-nowrap">{subItem.label}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="border-t border-gray-800 p-4">
          <div className="flex items-center space-x-3 overflow-hidden">
            <div className="w-8 h-8 rounded-full bg-[#CB3B0C] flex items-center justify-center flex-shrink-0">
              <span className="text-white font-semibold text-sm">{getInitials(user?.full_name)}</span>
            </div>
            {isExpanded && (
              <div className="overflow-hidden">
                <p className="text-white text-sm font-medium truncate">{user?.full_name || 'User'}</p>
                <p className="text-gray-400 text-xs truncate">{user?.email}</p>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};
